import { getAttributeValue, getDimensions } from '@boc/framework/products/attributes';
import { generateShortDescription } from '@boc/framework/products/description';

import { useProductDetails } from '~/framework/routes/products/providers';

const DescriptionSection = ({ children }: { children: React.ReactNode }) => {
  return <div className="flex flex-col md:flex-row">{children}</div>;
};

const DescriptionItem = ({ value }: { value: string | null }) => {
  if (!value) {
    return null;
  }

  return <p className="md:basis-2/3">{value}</p>;
};

const DescriptionHeading = ({ value }: { value: string | null }) => {
  if (!value) {
    return null;
  }

  return <p className="md:basis-1/3">{value}</p>;
};

const HeadingValueSection = ({
  heading,
  value,
  format = (v) => v,
}: {
  heading: string;
  value: string | null;
  format?: (v: string) => string;
}) => {
  if (!value) {
    return null;
  }

  return (
    <DescriptionSection>
      <DescriptionHeading value={heading} />
      <DescriptionItem value={format(value)} />
    </DescriptionSection>
  );
};

const ValuesSection = ({ value1, value2 }: { value1: string | null; value2: string | null }) => {
  return (
    <DescriptionSection>
      <DescriptionHeading value={value1} />
      <DescriptionItem value={value2} />
    </DescriptionSection>
  );
};

export const ShortDescription = () => {
  const { product } = useProductDetails();

  const shortDescription = generateShortDescription(product);

  return HeadingValueSection({ heading: 'What is it?', value: shortDescription });
};

const JigsawPuzzleKeyAttributes = () => {
  const {
    product: { attributes },
    variant,
  } = useProductDetails();

  const variantAttributes = variant?.attributes ?? [];

  const allAttributes = [...variantAttributes, ...attributes];

  return (
    <>
      <ValuesSection
        value1={getAttributeValue(allAttributes, 'puzzle-pieces', (pieces) => `${pieces} Pieces`)}
        value2={getAttributeValue(allAttributes, 'puzzle-cut')}
      />
      <HeadingValueSection
        heading="What's the size?"
        value={getDimensions(allAttributes)}
        format={(v) => `${v} finished dimensions`}
      />
    </>
  );
};

const DefaultKeyAttributes = () => {
  return <></>;
};

export const KeyAttributes = () => {
  const {
    product: {
      productType: { slug },
    },
  } = useProductDetails();

  switch (slug) {
    case 'jigsaw-puzzle':
      return <JigsawPuzzleKeyAttributes />;
    default:
      return <DefaultKeyAttributes />;
  }
};
